import React from 'react';
import { ExternalLink, Calendar } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';

const Background = () => {
  usePageTitle(); // Home page – uses default title
  
  const experiences = [
    {
      company: "Joveo",
      role: "Product Manager",
      duration: "June'24 - Present",
      description: "Building Intelligence in Ad trading",
      logo: "/logos/joveo_logo.webp",
      sections: [
        {
          title: "AutoTrader and Automations",
          achievements: [
            "Revolutionizing the complex landscape of job ad trading across multiple publishers through intelligent automation",
            "AutoTrader: Developed autonomous bidding algorithms for job advertisements across diverse job publishers with sophisticated goal management and performance optimization",
            "Publisher Budget Allocation: Engineered advanced yield curve estimation models to optimally distribute budgets across job boards, maximizing client ROI while minimizing cost per application",
            "Dynamic Margins: Implemented real-time margin adjustment algorithms that dynamically optimize pricing based on trading performance and customer goals, ensuring maximum revenue capture with zero dollar leakage"
          ]
        }
      ]
    },
    {
      company: "Delta Exchange",
      role: "Product Manager",
      duration: "Oct'22 - May'24 • 1 yr, 8 mos",
      description: "Leading product development across multiple revenue streams",
      logo: "/logos/delta_logo.svg",
      sections: [
        {
          title: "API Trading Revenue",
          achievements: [
            "Built integrations with Next Level Bot and Tradetron, resulting in a 30% increase in API Traders' Daily Active Users (DAU)",
            "Increased API Traders revenue by 300%",
            "Led Marketing initiatives like: Fee discount campaigns, YouTube videos showcasing profitable strategies to kick start the platforms with early users",
            "Authored and published insightful back test reports to encourage automated trading ideas and gained over 50K reads"
          ]
        },
        {
          title: "Signal Trading",
          achievements: [
            "Built a feature that converts TradingView indicators and strategies into automatic trading system",
            "TradingView generates alerts that user can forward to his Delta Exchange account automatically and deploy automated strategies",
            "Signal trading generates $200k in annual revenue"
          ]
        },
        {
          title: "Options Analytics",
          achievements: [
            "Built an Options Analytics suite, a graphical representation tool to visualize options data",
            "This increased user engagement by 5 minutes per session",
            "The tool attracts 20K Monthly Active Users",
            "Built analysis tools including Open Interest charts and Volatility graphs, providing traders with actionable insights"
          ]
        },
        {
          title: "New Revenue Stream - Debt on Demand",
          achievements: [
            "Launched an on-demand capital borrowing feature",
            "This led to a 15% increase in the Commission-to-Deposit Ratio",
            "Created a credit scoring algorithm factoring in trade frequency, win rate, repayment history, and wallet balance variability to effectively manage lender risk"
          ]
        }
      ]
    },
    {
      company: "GTO Inspector",
      role: "Founder & CEO",
      duration: "Nov'20 - Aug'22 • 1 yr, 10 mos",
      description: "Building advanced poker strategy tools and coaching business",
      logo: "/logos/gto_inspector_logo.webp",
      sections: [
        {
          title: "Poker Strategy Tool & Coaching Business",
          achievements: [
            "Built a tool that accurately identifies deviations from Game Theory Optimal (GTO) strategies in Pot-Limit Omaha (PLO) with its complex 270,000 starting hand combinations",
            "Initially developed for personal use, the tool gained interest among professional poker players for its accuracy and efficiency",
            "Shared an initial version with 10 members of a poker stable, receiving overwhelmingly positive feedback and requests for an expanded version"
          ]
        },
        {
          title: "Business Evolution",
          achievements: [
            "Initially focused on tool sales, but pivoted due to market limitations in niche poker tools",
            "Transitioned to establishing a poker coaching stable, integrating tool usage with personalized coaching, growing to 15 students at its peak",
          ]
        }
      ]
    },
    {
      company: "Two & Half Cents",
      role: "Founder & CEO",
      duration: "Sep'22 - May'24 • 1 yr, 8 mos",
      description: "Developing algorithmic trading systems and strategies",
      logo: "/logos/2andhalfcents_logo.webp",
      sections: [
        {
          title: "Key Achievements",
          achievements: [
            "Designed and executed multiple automated options trading strategies focused on capitalizing on theta decay in index options",
            "Developed an Options Pricing Model for NIFTY and BANKNIFTY, employing historical data analysis in similar VIX environments and running Monte Carlo simulations, significantly enhancing prediction accuracy",
            "Created the flagship (SaaS) product, \"Index Long Manager,\" a novel strategy employing bull call ratio spreads to maintain high leverage on index positions with limited downside risk",
            "Developed an in house python based backtesting engine, and evaluated 50+ futures and options strategies on tick by tick data",
            "Created systems to store and manage tick by tick data, making the data easily accessible though API calls to a private AWS server",
            "Two times Zerodha 60 day challenge winner"
          ]
        }
      ]
    },
    {
      company: "Shopahead",
      role: "Founder & CEO",
      duration: "Oct'21 - Jan'22 • 4 mos",
      description: "Drop-shipping business using Shopify, Facebook marketing, and Shiprocket logistics",
      logo: "/logos/shopahead_logo.webp",
      sections: [
        {
          title: "Business Performance",
          achievements: [
            "Ran this for 4 months and made ₹8 Lakhs in revenue. We fulfilled 648 orders",
            "Built a product analysis tool, leveraging Google Trends data to identify high-demand products that will generate a positive ROAS",
            "This tool increased the profitability rate of selected products to 50%, far exceeding the industry's average success rate of 10-20% (Profitability rate reflects the percentage of products that were profitable and did not incur losses on advertising)",
            "We also implemented an automated user consent system via WhatsApp, to reduce shipping to low intent buyers resulting in a 12% decrease in Return to Origin (RTO) rates",
            "COD orders have a very high rate of rejection, and shipping costs double when a parcel is rejected by the customer and it has to be Returned to the Origin. RTO is kryptonite for any e-commerce store"
          ]
        }
      ]
    },
    {
      company: "Futures First",
      role: "Commodity Trader (Intern)",
      duration: "Jan'19 - Jun'19 • 6 mos",
      description: "First professional trading experience",
      logo: "/logos/futures_first_logo.webp",
      sections: [
        {
          title: "Trading Experience",
          achievements: [
            "Traded Sugar (#SB11) futures at a proprietary trading firm, gaining valuable experience in financial markets and trading strategies"
          ]
        }
      ]
    },
    {
      company: "Indian Poker Pros",
      role: "Poker Player & Coach",
      duration: "Feb'18 - Dec'20 • 2 yrs 10 mos",
      description: "Professional poker playing and coaching at India's top poker stable",
      logo: "/logos/ipp_logo.webp",
      sections: [
        {
          title: "Professional Poker Career",
          achievements: [
            "Played and coached Texas No Limit Holdem and Pot Limit Omaha as part of the top Poker Stable in India - Indian Poker Pros",
            "Played the highest stakes. Overall winnings of more than ₹2.5Cr"
          ]
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <img
              src="/images/kunal-agarwal.jpg"
              alt="Kunal Agarwal"
              className="w-40 h-40 rounded-full mx-auto mb-8 shadow-xl ring-4 ring-white object-cover"
            />
            
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-8">
              Kunal Agarwal
            </h1>
            
            {/* Journey Timeline */}
            <div className="max-w-4xl mx-auto relative">
              {/* Timeline Line */}
              <div className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-gray-300 h-full"></div>
              
              <div className="space-y-16">
                {/* 2019: The Beginning */}
                <div className="relative">
                  <div className="absolute left-1/2 transform -translate-x-1/2 -translate-y-4">
                    <div className="w-20 h-20 bg-gray-900 rounded-2xl flex items-center justify-center shadow-lg z-10 relative">
                      <span className="text-white font-bold text-lg">2019</span>
                    </div>
                  </div>
                  
                  <div className="flex md:justify-start justify-center pt-12">
                    <div className="w-full max-w-lg md:mr-auto md:pr-16 px-4">
                      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-3">The Beginning</h3>
                        <p className="text-gray-700 leading-relaxed">
                          Fresh graduate from BITS Pilani, professional poker player, and... a losing trader. That's how my journey began. 
                          My first real taste of trading came through an internship at Futures First, where I traded SB11 (Sugar) futures 
                          on ICE using Technical Trader.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2020: Mathematics: My Edge */}
                <div className="relative">
                  <div className="absolute left-1/2 transform -translate-x-1/2 -translate-y-4">
                    <div className="w-20 h-20 bg-gray-900 rounded-2xl flex items-center justify-center shadow-lg z-10 relative">
                      <span className="text-white font-bold text-lg">2020</span>
                    </div>
                  </div>
                  
                  <div className="flex md:justify-end justify-center pt-12">
                    <div className="w-full max-w-lg md:ml-auto md:pl-16 px-4">
                      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-3">Mathematics: My Edge</h3>
                        <div className="space-y-3">
                          <p className="text-gray-700 leading-relaxed">
                            Understanding statistics, probability, and risk calculations from poker gave me a unique perspective. Evaluating every decision in life as a risk-reward trade-off.
                          </p>
                          <p className="text-gray-700 leading-relaxed">
                            Meanwhile, I created poker analysis tools with a developer I hired as an intern in his final year at BITS Pilani. 
                            These tools generated over <span className="font-semibold text-gray-900">₹1 Cr</span> in revenues by affiliating 
                            with poker stables in India like Indian Poker Pros and Poker Odyssey.
                          </p>
                          <p className="text-gray-700 leading-relaxed">
                            In 2020, I discovered algorithmic trading and everything clicked. The same mathematical frameworks that made 
                            me profitable in poker could be applied to markets.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2023: The Breakthrough */}
                <div className="relative">
                  <div className="absolute left-1/2 transform -translate-x-1/2 -translate-y-4">
                    <div className="w-20 h-20 bg-gray-900 rounded-2xl flex items-center justify-center shadow-lg z-10 relative">
                      <span className="text-white font-bold text-lg">2023</span>
                    </div>
                  </div>
                  
                  <div className="flex md:justify-start justify-center pt-12">
                    <div className="w-full max-w-lg md:mr-auto md:pr-16 px-4">
                      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-3">The Breakthrough</h3>
                        <p className="text-gray-700 leading-relaxed">
                          I started backtesting strategies in Python, expanded from equity F&O to CDS and commodities, and slowly 
                          transformed from a losing trader to generating consistent profits. Friends and relatives began asking me to manage their funds.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* NOW: Enter QuantCents */}
                <div className="relative">
                  <div className="absolute left-1/2 transform -translate-x-1/2 -translate-y-4">
                    <div className="w-20 h-20 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg z-10 relative">
                      <span className="text-white font-bold text-sm">NOW</span>
                    </div>
                  </div>
                  
                  <div className="flex md:justify-end justify-center pt-12">
                    <div className="w-full max-w-lg md:ml-auto md:pl-16 px-4">
                      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-3">Enter QuantCents</h3>
                        <p className="text-gray-700 leading-relaxed">
                          What started as friends asking for help became {' '}
                          <a href="https://www.quantcents.com" target="_blank" rel="noopener noreferrer" className="text-gray-900 hover:text-gray-700 font-semibold underline decoration-2 underline-offset-2">
                            QuantCents
                          </a> 
                          {' '}- a platform where I've poured years of systematic backtesting, algorithmic strategies, and real money experience. 
                          Built from curiosity, validated with <span className="font-semibold text-gray-900">₹52+ Lakhs</span> in documented trading profits.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Professional Experience</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A journey of building impactful products across fintech, gaming, and e-commerce
            </p>
          </div>
          
          <div className="space-y-12">
            {experiences.map((exp, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                {/* Company Header */}
                <div className="bg-gray-50 p-8 border-b border-gray-100">
                  <div className="flex items-start space-x-6">
                    <div className="w-16 h-16 bg-white rounded-xl shadow-md p-2 flex items-center justify-center flex-shrink-0">
                      <img 
                        src={exp.logo} 
                        alt={`${exp.company} logo`} 
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex flex-col md:flex-row md:items-center justify-between">
                        <div>
                          <h3 className="text-3xl font-bold text-gray-900 mb-1">{exp.company}</h3>
                          <p className="text-xl text-gray-600 mb-2">{exp.role}</p>
                          <p className="text-sm text-gray-500 font-medium">{exp.duration}</p>
                        </div>
                      </div>
                      <p className="text-gray-700 mt-4 text-lg font-medium">{exp.description}</p>
                    </div>
                  </div>
                </div>

                {/* Experience Sections */}
                <div className="p-8">
                  {exp.sections.map((section, sectionIndex) => (
                    <div key={sectionIndex} className={`${sectionIndex > 0 ? 'mt-8 pt-8 border-t border-gray-100' : ''}`}>
                      <h4 className="text-xl font-bold text-gray-800 mb-4">{section.title}</h4>
                      <ul className="space-y-3">
                        {section.achievements.map((achievement, achIndex) => (
                          <li key={achIndex} className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-gray-600 rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-gray-700 leading-relaxed">{achievement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Education Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Education</h2>
          </div>
          
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">BITS Pilani</h3>
              <p className="text-lg text-gray-600 mb-4">2014 - 2019</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                  <h4 className="text-xl font-semibold text-blue-900 mb-2">M.Sc. (Hons) in Biology</h4>
                  <p className="text-blue-700">Advanced studies in biological sciences</p>
                </div>
                
                <div className="bg-green-50 rounded-xl p-6 border border-green-100">
                  <h4 className="text-xl font-semibold text-green-900 mb-2">B.E (Hons) in Civil Engineering</h4>
                  <p className="text-green-700">Engineering foundation with analytical thinking</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-6">
            Let's Connect
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Interested in discussing product strategy, trading systems, or potential collaborations? 
            I'd love to hear from you.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://calendly.com/kunal-twoandhalfcents/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-xl hover:bg-gray-50 transition-colors duration-200 shadow-lg"
            >
              <Calendar className="mr-2 w-5 h-5" />
              Schedule a Meeting
            </a>
            
            <a
              href="https://www.linkedin.com/in/kunal35/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-transparent text-white font-semibold rounded-xl border-2 border-white hover:bg-white hover:text-gray-900 transition-all duration-200"
            >
              <ExternalLink className="mr-2 w-5 h-5" />
              Connect on LinkedIn
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Background;